import React, { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { MainLayout } from "@/components/layout/MainLayout";
import { motion } from "framer-motion";
import { Music, User, Play, RefreshCw } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useTranslation } from "@/hooks/useTranslation";
import { cn } from "@/lib/utils";
import { supabase } from "@/integrations/supabase/client";
import SpotifyWebPlayer from "@/components/spotify/SpotifyWebPlayer";
import SpotifyEmbedPlayer from "@/components/spotify/SpotifyEmbedPlayer";

const SPOTIFY_CLIENT_ID = "fbe23ff36e9b4f1a829bdd230f1013d7";
const REDIRECT_URI = window.location.origin + "/music";
const SCOPES = [
  "user-read-private",
  "user-read-email",
  "playlist-read-private",
  "user-library-read",
  "streaming",
  "user-read-playback-state",
  "user-modify-playback-state",
].join(" ");

interface SpotifyUser {
  display_name: string;
  images: { url: string }[];
  product?: string; // "premium" | "free" | "open"
}

interface SpotifyPlaylist {
  id: string;
  name: string;
  images: { url: string }[];
  tracks: { total: number };
}

// PKCE helpers
function generateRandomString(length: number): string {
  const possible = "ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789";
  const values = crypto.getRandomValues(new Uint8Array(length));
  return Array.from(values, (x) => possible[x % possible.length]).join("");
}

async function sha256(plain: string): Promise<ArrayBuffer> {
  const encoder = new TextEncoder();
  return crypto.subtle.digest("SHA-256", encoder.encode(plain));
}

function base64urlEncode(buffer: ArrayBuffer): string {
  const bytes = new Uint8Array(buffer);
  let str = "";
  bytes.forEach((b) => (str += String.fromCharCode(b)));
  return btoa(str).replace(/\+/g, "-").replace(/\//g, "_").replace(/=+$/, "");
}

const PLAYLISTS_PER_ROW = 4;

export default function Spotify() {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [accessToken, setAccessToken] = useState<string | null>(null);
  const [refreshToken, setRefreshToken] = useState<string | null>(null);
  const [user, setUser] = useState<SpotifyUser | null>(null);
  const [playlists, setPlaylists] = useState<SpotifyPlaylist[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [authError, setAuthError] = useState<string | null>(null);
  const [playlistError, setPlaylistError] = useState<string | null>(null);
  const [activePlaylistId, setActivePlaylistId] = useState<string | null>(null);
  const processingCode = useRef(false);

  const isPremium = user?.product === "premium";

  // Handle callback with authorization code
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const code = params.get("code");
    const error = params.get("error");

    if (error) {
      setAuthError(error);
      window.history.replaceState({}, document.title, window.location.pathname);
      return;
    }

    if (code && !processingCode.current) {
      processingCode.current = true;
      window.history.replaceState({}, document.title, window.location.pathname);
      exchangeCode(code);
    } else {
      const stored = localStorage.getItem("spotify_access_token");
      const storedRefresh = localStorage.getItem("spotify_refresh_token");
      if (stored) {
        setAccessToken(stored);
        if (storedRefresh) setRefreshToken(storedRefresh);
      }
    }
  }, []);

  useEffect(() => {
    if (accessToken) {
      fetchUserData();
      fetchPlaylists();
    }
  }, [accessToken]);

  const exchangeCode = async (code: string) => {
    setIsLoading(true);
    setAuthError(null);
    try {
      const codeVerifier = localStorage.getItem("spotify_code_verifier");
      if (!codeVerifier) {
        setAuthError("Missing code verifier. Please try logging in again.");
        setIsLoading(false);
        return;
      }

      const { data, error } = await supabase.functions.invoke("spotify-auth", {
        body: {
          action: "exchange",
          code,
          redirect_uri: REDIRECT_URI,
          code_verifier: codeVerifier,
        },
      });

      localStorage.removeItem("spotify_code_verifier");

      if (error || data?.error) {
        setAuthError(data?.error || error?.message || "Token exchange failed");
        setIsLoading(false);
        return;
      }

      setAccessToken(data.access_token);
      localStorage.setItem("spotify_access_token", data.access_token);

      if (data.refresh_token) {
        setRefreshToken(data.refresh_token);
        localStorage.setItem("spotify_refresh_token", data.refresh_token);
      }
    } catch {
      setAuthError("Failed to connect to Spotify");
    }
    setIsLoading(false);
  };

  const refreshAccessToken = async (): Promise<string | null> => {
    if (!refreshToken) {
      console.error("[Spotify] No refresh token available");
      return null;
    }
    try {
      console.error("[Spotify] Refreshing access token...");
      const { data, error } = await supabase.functions.invoke("spotify-auth", {
        body: { action: "refresh", refresh_token: refreshToken },
      });

      if (error || data?.error) {
        console.error("[Spotify] Token refresh failed:", error || data?.error);
        return null;
      }

      console.error("[Spotify] Token refreshed successfully");
      setAccessToken(data.access_token);
      localStorage.setItem("spotify_access_token", data.access_token);

      if (data.refresh_token) {
        setRefreshToken(data.refresh_token);
        localStorage.setItem("spotify_refresh_token", data.refresh_token);
      }

      return data.access_token;
    } catch (err) {
      console.error("[Spotify] Token refresh error:", err);
      return null;
    }
  };

  const spotifyFetch = async (url: string): Promise<Response | null> => {
    if (!accessToken) return null;

    let response = await fetch(url, {
      headers: { Authorization: `Bearer ${accessToken}` },
    });

    if (response.status === 401) {
      const newToken = await refreshAccessToken();
      if (newToken) {
        response = await fetch(url, {
          headers: { Authorization: `Bearer ${newToken}` },
        });
      } else {
        handleLogout();
        return null;
      }
    }

    return response;
  };

  const fetchUserData = async () => {
    const response = await spotifyFetch("https://api.spotify.com/v1/me");
    if (response?.ok) {
      const data = await response.json();
      setUser(data);
      console.error("[Spotify] User data loaded:", data.display_name, "| Product:", data.product);
    } else {
      console.error("[Spotify] Failed to fetch user data, status:", response?.status);
    }
  };

  const fetchPlaylists = async () => {
    setIsLoading(true);
    setPlaylistError(null);
    try {
      const response = await spotifyFetch("https://api.spotify.com/v1/me/playlists?limit=20");
      if (response?.ok) {
        const data = await response.json();
        const items = data.items || [];
        setPlaylists(items);
        console.error("[Spotify] Loaded", items.length, "playlists");
        if (items.length === 0) {
          setPlaylistError("No playlists found. Create playlists in Spotify first.");
        }
      } else if (response) {
        const errorText = await response.text();
        console.error("[Spotify] Playlist fetch failed, status:", response.status, "body:", errorText);
        if (response.status === 403) {
          // 403 = scopes changed or account restriction; force re-auth
          console.error("[Spotify] 403 Forbidden — clearing tokens and forcing re-auth");
          handleLogout();
          setPlaylistError("Spotify permissions expired. Please log in again.");
        } else {
          setPlaylistError(`Spotify API error (${response.status}). Try disconnecting and reconnecting.`);
        }
      } else {
        console.error("[Spotify] Playlist fetch returned null (likely token expired)");
        setPlaylistError("Session expired. Please reconnect to Spotify.");
      }
    } catch (err) {
      console.error("[Spotify] Playlist fetch error:", err);
      setPlaylistError("Failed to load playlists. Please try again.");
    }
    setIsLoading(false);
  };

  const handleLogin = async () => {
    const codeVerifier = generateRandomString(64);
    localStorage.setItem("spotify_code_verifier", codeVerifier);

    const hashed = await sha256(codeVerifier);
    const codeChallenge = base64urlEncode(hashed);

    const params = new URLSearchParams({
      client_id: SPOTIFY_CLIENT_ID,
      response_type: "code",
      redirect_uri: REDIRECT_URI,
      scope: SCOPES,
      code_challenge_method: "S256",
      code_challenge: codeChallenge,
    });

    const authUrl = `https://accounts.spotify.com/authorize?${params.toString()}`;

    if (window.top && window.top !== window.self) {
      window.top.location.href = authUrl;
    } else {
      window.location.href = authUrl;
    }
  };

  const handleLogout = () => {
    setAccessToken(null);
    setRefreshToken(null);
    setUser(null);
    setPlaylists([]);
    setActivePlaylistId(null);
    localStorage.removeItem("spotify_access_token");
    localStorage.removeItem("spotify_refresh_token");
    localStorage.removeItem("spotify_code_verifier");
  };

  const openPlaylist = (playlistId: string) => {
    setActivePlaylistId(playlistId);
  };


  if (isLoading && !accessToken) {
    return (
      <MainLayout>
        <div
          className="flex-1 flex flex-col items-center justify-center p-6"
          style={{ background: "linear-gradient(180deg, #1a1a1a 0%, #121212 100%)" }}
        >
          <RefreshCw className="w-12 h-12 text-green-500 animate-spin" />
          <p className="text-white mt-4">{t("connectingToPlayer")}</p>
        </div>
      </MainLayout>
    );
  }

  if (!accessToken) {
    return (
      <MainLayout>
        <div
          className="flex-1 flex flex-col items-center justify-center p-6"
          style={{ background: "linear-gradient(180deg, #1a1a1a 0%, #121212 100%)" }}
          dir="rtl"
        >
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="text-center space-y-6">
            <div className="w-32 h-32 bg-[#1DB954] rounded-full flex items-center justify-center mx-auto">
              <Music className="w-16 h-16 text-black" />
            </div>
            <h1 className="text-3xl font-bold text-white">{t("welcomeToSpotify")}</h1>
            {authError && (
              <p className="text-red-400 text-sm max-w-md mx-auto">{authError}</p>
            )}
            <Button
              onClick={handleLogin}
              data-scan-item
              className="h-14 px-12 rounded-full bg-[#1DB954] hover:bg-[#1ed760] text-black font-bold text-lg"
            >
              <User className="w-5 h-5 ml-2" />
              {t("loginToSpotify")}
            </Button>
          </motion.div>
        </div>
      </MainLayout>
    );
  }

  return (
    <MainLayout>
      <div
        className="flex-1 flex flex-col overflow-hidden"
        style={{ background: "linear-gradient(180deg, #1a1a1a 0%, #121212 100%)" }}
        dir="rtl"
      >
        <div className="flex items-center justify-between p-4 border-b border-white/10">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-[#1DB954] rounded-lg flex items-center justify-center">
              <Music className="w-6 h-6 text-black" />
            </div>
            <div>
              <h1 className="text-xl font-bold text-white">{t("spotify")}</h1>
              {user && (
                <p className="text-sm text-gray-400">
                  {t("hello")}, {user.display_name}
                </p>
              )}
            </div>
          </div>
          <Button
            variant="ghost"
            size="sm"
            onClick={handleLogout}
            data-scan-item
            className="text-gray-400 hover:text-white"
          >
            {t("disconnectSpotify")}
          </Button>
        </div>

        <div className="flex-1 p-6 overflow-auto">
          {activePlaylistId && accessToken && (
            isPremium ? (
              <SpotifyWebPlayer
                accessToken={accessToken}
                playlistId={activePlaylistId}
                onClose={() => setActivePlaylistId(null)}
              />
            ) : (
              <SpotifyEmbedPlayer
                playlistId={activePlaylistId}
                onClose={() => setActivePlaylistId(null)}
              />
            )
          )}
          <h2 className="text-xl font-semibold text-white mb-6">{t("yourPlaylists")}</h2>
          {isLoading ? (
            <div className="flex items-center justify-center py-12">
              <RefreshCw className="w-8 h-8 text-[#1DB954] animate-spin" />
            </div>
          ) : playlistError ? (
            <div className="flex flex-col items-center justify-center py-12 space-y-4">
              <p className="text-gray-400 text-center max-w-md">{playlistError}</p>
              <Button
                onClick={() => fetchPlaylists()}
                className="bg-[#1DB954] hover:bg-[#1ed760] text-black font-bold"
              >
                <RefreshCw className="w-4 h-4 ml-2" />
                {t("tryAgain") || "Try Again"}
              </Button>
            </div>
          ) : (
            <motion.div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4">
              {playlists.map((playlist) => (
                  <motion.div
                    key={playlist.id}
                    data-scan-item
                    className="group cursor-pointer p-3 rounded-lg bg-white/5 hover:bg-white/10 transition-colors"
                    onClick={() => openPlaylist(playlist.id)}
                  >
                    <div className="relative aspect-square mb-3 rounded-md overflow-hidden bg-white/10">
                      {playlist.images?.[0]?.url ? (
                        <img
                          src={playlist.images[0].url}
                          alt={playlist.name}
                          className="w-full h-full object-cover"
                        />
                      ) : (
                        <div className="w-full h-full flex items-center justify-center">
                          <Music className="w-12 h-12 text-gray-500" />
                        </div>
                      )}
                      <div className="absolute bottom-2 left-2 opacity-0 group-hover:opacity-100 transition-opacity">
                        <div className="w-12 h-12 bg-[#1DB954] rounded-full flex items-center justify-center shadow-xl">
                          <Play className="w-6 h-6 text-black fill-black mr-[-2px]" />
                        </div>
                      </div>
                    </div>
                    <h3 className="font-medium text-white truncate">{playlist.name}</h3>
                    <p className="text-sm text-gray-400">
                      {playlist.tracks.total} {t("songsCount")}
                    </p>
                  </motion.div>
              ))}
            </motion.div>
          )}
        </div>
      </div>
    </MainLayout>
  );
}
